const solutions = [
  {
    id: 'residential',
    badge: 'For Homes',
    badgeColor: 'bg-primary-100 text-primary-700',
    title: 'Residential Solar',
    subtitle: 'Power your home with clean energy',
    description: 'A typical Australian home uses 15–25 kWh per day. A well-sized residential solar system — usually 6.6kW to 13.3kW — can offset 60–100% of your household electricity consumption, dramatically reducing your power bills.',
    image: 'https://readdy.ai/api/search-image?query=Beautiful%20modern%20Australian%20family%20home%20with%20sleek%20black%20solar%20panels%20covering%20entire%20north-facing%20roof%2C%20lush%20garden%2C%20bright%20sunny%20day%2C%20clean%20contemporary%20architecture%2C%20warm%20inviting%20atmosphere%2C%20editorial%20real%20estate%20photography%20style&width=800&height=560&seq=solar-residential-au-2026&orientation=landscape',
    features: [
      'Systems from 6.6kW to 13.3kW',
      'Panel brands including LG, SunPower & Jinko',
      'String inverters & microinverter options',
      'Compatible with major battery brands',
      'CEC-accredited installation guaranteed',
      'Monitoring app included',
    ],
    savings: 'Save up to $2,000/year',
    savingsColor: 'text-primary-600',
    cta: 'Get Residential Solar Quotes',
  },
  {
    id: 'commercial',
    badge: 'For Businesses',
    badgeColor: 'bg-secondary-100 text-secondary-700',
    title: 'Commercial Solar',
    subtitle: 'Cut operating costs with large-scale solar',
    description: 'Australian businesses are rapidly adopting commercial solar to reduce operating costs and demonstrate sustainability commitments. Commercial systems — typically 30kW to 1MW — deliver payback periods of just 3–5 years and multi-decade returns.',
    image: 'https://readdy.ai/api/search-image?query=Large%20Australian%20commercial%20warehouse%20or%20factory%20with%20massive%20solar%20panel%20array%20on%20flat%20industrial%20roof%2C%20clear%20blue%20sky%2C%20aerial%20or%20elevated%20angle%20shot%2C%20professional%20industrial%20photography%2C%20sunlight%20glinting%20off%20panels%2C%20green%20energy%20concept&width=800&height=560&seq=solar-commercial-au-2026&orientation=landscape',
    features: [
      'Systems from 30kW to 1MW+',
      'Roof, ground-mount & carport options',
      'Power Purchase Agreements (PPAs) available',
      'Battery + demand management solutions',
      'Commercial finance & tax incentives',
      'Dedicated project management',
    ],
    savings: 'Payback in 3–5 years',
    savingsColor: 'text-secondary-600',
    cta: 'Get Commercial Solar Quotes',
  },
  {
    id: 'battery',
    badge: 'Battery Storage',
    badgeColor: 'bg-accent-100 text-accent-800',
    title: 'Solar Battery Storage',
    subtitle: 'Store your solar and use it when you need it',
    description: 'Battery storage transforms your solar system from a daytime-only solution to a 24/7 energy source. Store excess solar during the day and power your home through the evening — or add a backup power capability for outages.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20sleek%20home%20battery%20storage%20unit%20mounted%20on%20garage%20wall%20of%20contemporary%20Australian%20home%2C%20glowing%20LED%20display%20showing%20charge%20level%2C%20solar%20panels%20visible%20on%20roof%20through%20window%2C%20clean%20modern%20interior%20garage%2C%20premium%20product%20photography%20style&width=800&height=560&seq=solar-battery-au-2026&orientation=landscape',
    features: [
      'Brands: Tesla Powerwall, SolarEdge, Sungrow',
      'Capacity from 5kWh to 30kWh',
      'Time-of-use tariff optimisation',
      'Blackout protection / backup power',
      'VPP participation ready',
      'Up to $2,950 state rebate available',
    ],
    savings: 'Up to $2,950 rebate',
    savingsColor: 'text-accent-800',
    cta: 'Explore Battery Storage Options',
  },
];

export default function SolarSolutions() {
  return (
    <section className="bg-background-100/50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200/50 mb-5">
            <i className="ri-stack-line text-accent-700 text-sm"></i>
            <span className="text-xs font-semibold text-accent-800 tracking-wide uppercase whitespace-nowrap">Solar Solutions</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            Solar Energy Solutions<br className="hidden md:block" /> for Every Need
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Whether you're a homeowner, a business, or looking to add battery storage — AK Energy connects you with the right solar solution and trusted providers.
          </p>
        </div>

        <div className="space-y-10 md:space-y-14">
          {solutions.map((sol, idx) => (
            <article
              key={sol.id}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
            >
              {/* Image */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={sol.image}
                    alt={`${sol.title} in Australia — AK Energy`}
                    className="w-full h-72 md:h-80 object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${sol.badgeColor}`}>
                      {sol.badge}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2">
                    <span className={`text-sm font-bold ${sol.savingsColor}`}>{sol.savings}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 mb-2">{sol.title}</h3>
                <p className="text-sm font-semibold text-primary-600 mb-4">{sol.subtitle}</p>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">{sol.description}</p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-7">
                  {sol.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-500 text-base mt-0.5 shrink-0"></i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="/#contact"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-xl bg-foreground-950 text-white hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
                >
                  {sol.cta}
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}